import { ReactElement, useEffect, useRef, useState } from "react";
import { MONTHS } from "../../../../../../constants/Months";

import { TDriverGrowthMetric } from "../../../../../../contexts/account/data/logic/driversAndWorksheetData";
import { MonthlyAdjustmentType, MonthlyAverageBasePeriod } from "../../../../../../__generated__/generated_types";

import { deleteNodeKeys, TFormulaNodeProps } from "./logic/formulaNode";
import * as css from "./styles/css.module.scss";
import {IFormulaBarUpdates} from "../../../../../../contexts/account/data/useFormulaBarUpdates";
import { Property } from "../../../../../../contexts/properties/logic";
import YearlyAverageMenu from "../formula-menu/YearlyAverageMenu";


type TGrowthDriverFxNodeProps = TFormulaNodeProps&{
    driver: TDriverGrowthMetric,
    property: Property,
}

export const MonthlyAverageBasePeriodInverseMap = {
    [MonthlyAverageBasePeriod.LastMonth]: 1,
    [MonthlyAverageBasePeriod.LastThreeMonthsActuals]: 3,
    [MonthlyAverageBasePeriod.LastSixMonthsActuals]: 6,
    [MonthlyAverageBasePeriod.LastTwelveMonthsActuals]: 12,
};


export function renderBasePeriod(
    driver: TDriverGrowthMetric,
    asJSX: undefined | {
        locked: boolean,
        fbUpdates: IFormulaBarUpdates
    },
    property: Property,
): string | JSX.Element {
    let basePeriodNodeText = '';
    if (driver.monthlyAverageBasePeriod === MonthlyAverageBasePeriod.CustomLookbackPeriod) {
        if (driver.lookbackPeriodStart && driver.lookbackPeriodEnd) {
            const startMonth = driver.lookbackPeriodStart.split("-")[1];
            const endMonth = driver.lookbackPeriodEnd.split("-")[1];
            const startYear = driver.lookbackPeriodStart.split("-")[0];
            const endYear = driver.lookbackPeriodEnd.split("-")[0];
            let periodStart, periodEnd;

            if (startMonth && endMonth && startYear && endYear) {
                if (property.isAcquisitionProperty) {
                    periodStart = `${MONTHS[parseInt(startMonth) - 1]} T12`;
                    periodEnd = `${MONTHS[parseInt(endMonth) - 1]} T12`;
                } else {
                    periodStart = `${MONTHS[parseInt(startMonth) - 1]} ${parseInt(startYear)}`;
                    periodEnd = `${MONTHS[parseInt(endMonth) - 1]} ${parseInt(endYear)}`;
                }
            }

            basePeriodNodeText = `${periodStart} to ${periodEnd}`;
        }
    }

    if (!asJSX) {
        return basePeriodNodeText;
    }
    else if (asJSX.locked) {
        return <>&nbsp;{basePeriodNodeText}</>;
    }
    return (
        <YearlyAverageMenu
            fbUpdates={asJSX.fbUpdates}
            level={0}
            triggerNode={<a>{basePeriodNodeText}</a>}
        />
    );
}

export function renderYearlyAdjustment(
    driver: TDriverGrowthMetric,
    asJSX: undefined | {
        locked: boolean,
        fbUpdates: IFormulaBarUpdates
    }
): string | JSX.Element {
    let yearlyAdjustmentNodeText = '';
    // '!=' on purpose to check for null and undefined
    if (driver.monthlyAdjustmentType === MonthlyAdjustmentType.Dollar && driver.monthlyAdjustmentValue != null) {
        yearlyAdjustmentNodeText = `$${driver.monthlyAdjustmentValue} yearly adjustment`;
    }
    if (driver.monthlyAdjustmentType === MonthlyAdjustmentType.Percent && driver.monthlyAdjustmentValue != null) {
        yearlyAdjustmentNodeText = `${driver.monthlyAdjustmentValue}% yearly adjustment`;
    }

    if (!asJSX) {
        return yearlyAdjustmentNodeText;
    }
    else if (asJSX.locked) {
        return <>&nbsp;{yearlyAdjustmentNodeText}</>;
    }
    return (
        <YearlyAverageMenu
            fbUpdates={asJSX.fbUpdates}
            level={1}
            triggerNode={<a>{yearlyAdjustmentNodeText}</a>}
        />
    );
}

export default function(props: TGrowthDriverFxNodeProps): ReactElement {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [childNodeHovered, setChildNodeHovered] = useState<boolean>(false);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isFocused && wrapperRef.current) {
            wrapperRef.current.focus();
        }
    }, [isFocused]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                (wrapperRef.current === event.target ||
                wrapperRef.current.contains(event.target as Node))
            ) {
                return;
            }
            setIsFocused(false);
        };

        // Clicking outside the current node should remove focus
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClick = () => {
        setIsFocused(!isFocused);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (
            isFocused && deleteNodeKeys.includes(e.key) && !childNodeHovered
        ) {
            props.fbUpdates.removeYearlyAverageGrowthDriver();
        }
    };

    const nodeClassName = props.locked ? css.fxNodeLocked : css.fxNode;
    const className = `${nodeClassName} ${props.className ? props.className : ''} ${css.deletableNode} ${isFocused ? css.focusedNode : ''} ${childNodeHovered ? css.disableHover : ''}`;

    return (
        <div
            className={className}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setChildNodeHovered(false)}
            ref={wrapperRef}
            tabIndex={0}
        >
            (Yearly Average:
            <div
                className={css.fxNode}
                onMouseEnter={() => setChildNodeHovered(true)}
                onMouseLeave={() => setChildNodeHovered(false)}
            >
                {renderBasePeriod(props.driver, {...props}, props.property)}
            </div>
            &nbsp;with&nbsp;
            <div
                className={css.fxNode}
                onMouseEnter={() => setChildNodeHovered(true)}
                onMouseLeave={() => setChildNodeHovered(false)}
            >
                {renderYearlyAdjustment(props.driver, {...props})}
            </div>)
        </div>
    );
}
